import { Link } from 'react-router-dom'
import type { KeyLocationAccordionSection } from '@buxus/shared'
import { formatPhoneDisplay, phoneTelHref } from '@buxus/shared'
import type { ScreenInteractionContext } from './platform/analytics/interactionAnalytics'
import { trackLinkPressed, trackPhonePressed } from './platform/analytics/interactionAnalytics'
import { BuxusAccordion } from './components/BuxusAccordion'
import { BuxusDataCardRow, BuxusDataCardTable } from './components/BuxusDataCardTable'
import { pathForMenuStackPrefix } from './navigation/appPaths'

type Props = {
  sections: KeyLocationAccordionSection[]
  operatorId: string
  activeAssetId: string
  urlSlugForOperator: (operatorId: string) => string
  analytics?: ScreenInteractionContext
}

function PhoneValue({
  value,
  contact,
  analytics,
}: {
  value: string
  contact: string
  analytics?: ScreenInteractionContext
}) {
  const display = formatPhoneDisplay(value)
  const href = phoneTelHref(value)
  if (!display) return <>—</>
  return href ? (
    <a
      className="buxus-theme-link"
      href={href}
      onClick={
        analytics
          ? () => trackPhonePressed({ ...analytics, phone: value, contact })
          : undefined
      }
    >
      {display}
    </a>
  ) : (
    <span>{display}</span>
  )
}

/** Mobile KeyLocationsScreen accordion parity. */
export function KeyLocationAccordions({
  sections,
  operatorId,
  activeAssetId,
  urlSlugForOperator,
  analytics,
}: Props) {
  const defaultExpandedIds = sections.length === 1 ? [sections[0]!.id] : undefined
  const overviewTo = pathForMenuStackPrefix(
    operatorId,
    ['screen.assets', 'screen.assetOverview'],
    activeAssetId,
    urlSlugForOperator,
  )

  return (
    <BuxusAccordion
      className="key-location-accordion"
      defaultExpandedIds={defaultExpandedIds}
      analytics={analytics}
      items={sections.map((section) => {
        const hasCoords = !!(section.lat && section.lng)
        const latLongLabel = hasCoords ? `${section.lat}, ${section.lng}` : ''
        const canJumpToMap = hasCoords && !!section.zoomLevel

        return {
          id: section.id,
          title: section.title,
          description: section.subtitle || undefined,
          content: (
            <>
              {section.description ? (
                <p className="key-location-description">{section.description}</p>
              ) : null}
              <BuxusDataCardTable>
                <BuxusDataCardRow label="Phone Number 1">
                  <PhoneValue
                    value={section.phoneNumber1}
                    contact="Phone Number 1"
                    analytics={analytics}
                  />
                </BuxusDataCardRow>
                <BuxusDataCardRow label="Phone Number 2">
                  <PhoneValue
                    value={section.phoneNumber2}
                    contact="Phone Number 2"
                    analytics={analytics}
                  />
                </BuxusDataCardRow>
                <BuxusDataCardRow label="Map Code">{section.mapCode || '—'}</BuxusDataCardRow>
                <BuxusDataCardRow label="Lat / Long">
                  {latLongLabel ? (
                    canJumpToMap ? (
                      <Link
                        className="buxus-theme-link key-location-coords"
                        to={overviewTo}
                        state={{
                          assetMapJump: {
                            lat: section.lat,
                            lng: section.lng,
                            zoom: section.zoomLevel,
                          },
                        }}
                        onClick={() => {
                          if (analytics && latLongLabel) {
                            trackLinkPressed({
                              ...analytics,
                              linkTarget: overviewTo,
                              title: latLongLabel,
                              navigationReason: 'key_location_coordinates',
                              latitude: section.lat,
                              longitude: section.lng,
                            })
                          }
                        }}
                      >
                        {latLongLabel}
                      </Link>
                    ) : (
                      <span className="key-location-coords">{latLongLabel}</span>
                    )
                  ) : (
                    '—'
                  )}
                </BuxusDataCardRow>
              </BuxusDataCardTable>
            </>
          ),
        }
      })}
    />
  )
}
